<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import { reportIngestTokensApi, customersApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import {
    Key, Plus, Trash2, Copy, Check, Loader, AlertCircle, CheckCircle, X, ShieldAlert
  } from 'lucide-svelte';

  let loading = true;
  let creating = false;
  let message = null;
  let tokens = [];
  let customers = [];

  // Create form
  let newName = '';
  let selectedScope = []; // customer ids; empty = any

  // Freshly minted token (shown once)
  let mintedToken = null;
  let copied = false;

  // Revoke confirmation
  let revokingId = null;

  onMount(() => {
    loadTokens();
    loadCustomers();
  });

  async function loadTokens() {
    loading = true;
    try {
      const response = await reportIngestTokensApi.list();
      tokens = response.tokens || [];
    } catch (err) {
      console.error('[AdminTokenPanel] Error loading tokens:', err);
      message = { type: 'error', text: err.message };
    } finally {
      loading = false;
    }
  }

  async function loadCustomers() {
    try {
      const response = await customersApi.getAll();
      customers = response.customers || [];
    } catch (err) {
      console.error('[AdminTokenPanel] Error loading customers:', err);
    }
  }

  async function createToken() {
    if (!newName.trim()) {
      message = { type: 'error', text: $_('ingestTokens.nameRequired') };
      return;
    }
    creating = true;
    message = null;
    try {
      const scope = selectedScope.length > 0 ? selectedScope : null;
      mintedToken = await reportIngestTokensApi.create(newName.trim(), scope);
      newName = '';
      selectedScope = [];
      await loadTokens();
    } catch (err) {
      console.error('[AdminTokenPanel] Error creating token:', err);
      message = { type: 'error', text: err.message };
    } finally {
      creating = false;
    }
  }

  async function copyToken(value) {
    try {
      await navigator.clipboard.writeText(value);
      copied = true;
      setTimeout(() => (copied = false), 2000);
    } catch (err) {
      console.error('[AdminTokenPanel] Copy failed:', err);
    }
  }

  async function revokeToken(id) {
    try {
      await reportIngestTokensApi.revoke(id);
      message = { type: 'success', text: $_('ingestTokens.revokeSuccess') };
      revokingId = null;
      await loadTokens();
    } catch (err) {
      console.error('[AdminTokenPanel] Error revoking token:', err);
      message = { type: 'error', text: err.message };
    }
  }

  function formatDateTime(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
  }
</script>

<Layout>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center space-x-4">
      <div class="p-3 bg-linear-to-br from-secondary-500 to-secondary-600 rounded-2xl shadow-lg" style="background: linear-gradient(to bottom right, #ff00ff, #cc00cc);">
        <Key size={28} class="text-white" />
      </div>
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{$_('ingestTokens.title')}</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400">{$_('ingestTokens.subtitle')}</p>
      </div>
    </div>

    {#if message}
      <div class="p-4 rounded-lg flex items-center space-x-2 {message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}">
        {#if message.type === 'success'}<CheckCircle size={20} />{:else}<AlertCircle size={20} />{/if}
        <span>{message.text}</span>
        <button class="ml-auto hover:opacity-70" onclick={() => message = null}>&times;</button>
      </div>
    {/if}

    <!-- Freshly minted token (shown once) -->
    {#if mintedToken}
      <div class="card p-5 border-2 border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20">
        <div class="flex items-start space-x-2 mb-3">
          <ShieldAlert size={20} class="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 class="font-bold text-amber-800 dark:text-amber-200">{$_('ingestTokens.shownOnceTitle')}</h3>
            <p class="text-sm text-amber-700 dark:text-amber-300">{$_('ingestTokens.shownOnceHint')}</p>
          </div>
          <button class="ml-auto text-amber-600 hover:text-amber-800" onclick={() => mintedToken = null}><X size={18} /></button>
        </div>
        <div class="flex items-center gap-2">
          <code class="flex-1 px-3 py-2 bg-white dark:bg-gray-900 border border-amber-300 dark:border-amber-700 rounded font-mono text-sm break-all">{mintedToken.token}</code>
          <button class="btn btn-secondary flex items-center space-x-1" onclick={() => copyToken(mintedToken.token)}>
            {#if copied}<Check size={16} />{:else}<Copy size={16} />{/if}
            <span>{copied ? $_('ingestTokens.copied') : $_('common.copy')}</span>
          </button>
        </div>
      </div>
    {/if}

    <!-- Create form -->
    <div class="card p-6">
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
        <Plus size={20} class="text-primary-500" />
        <span>{$_('ingestTokens.create')}</span>
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label class="label">{$_('ingestTokens.name')}</label>
          <input type="text" class="input w-full" placeholder={$_('ingestTokens.namePlaceholder')} bind:value={newName} />
        </div>
        <div>
          <label class="label">{$_('ingestTokens.scope')}</label>
          <select class="input w-full" multiple size="4" bind:value={selectedScope}>
            {#each customers as customer}
              <option value={customer.id}>{customer.companyName || `${customer.name} ${customer.surname}`}</option>
            {/each}
          </select>
          <p class="text-xs text-gray-400 mt-1">{$_('ingestTokens.scopeHint')}</p>
        </div>
        <div class="flex items-end">
          <button class="btn btn-primary w-full flex items-center justify-center space-x-2" onclick={createToken} disabled={creating || !newName.trim()}>
            {#if creating}<Loader size={18} class="animate-spin" />{:else}<Plus size={18} />{/if}
            <span>{$_('ingestTokens.createButton')}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Token list -->
    {#if loading}
      <div class="card p-12 text-center">
        <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
      </div>
    {:else if tokens.length === 0}
      <div class="card p-12 text-center">
        <Key size={48} class="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('ingestTokens.noTokens')}</p>
      </div>
    {:else}
      <div class="card overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('ingestTokens.name')}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('ingestTokens.scope')}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('ingestTokens.lastUsed')}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('ingestTokens.statusCol')}</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('common.actions')}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              {#each tokens as token (token.id)}
                <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50 {token.revokedAt ? 'opacity-50' : ''}">
                  <td class="px-4 py-4 font-medium text-gray-900 dark:text-white">{token.name}</td>
                  <td class="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                    {token.customerScope?.length ? token.customerScope.join(', ') : $_('ingestTokens.anyCustomer')}
                  </td>
                  <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{formatDateTime(token.lastUsedAt)}</td>
                  <td class="px-4 py-4">
                    {#if token.revokedAt}
                      <span class="px-2 py-0.5 text-xs font-bold border rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-300">{$_('ingestTokens.revoked')}</span>
                    {:else}
                      <span class="px-2 py-0.5 text-xs font-bold border rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300">{$_('ingestTokens.activeState')}</span>
                    {/if}
                  </td>
                  <td class="px-4 py-4 text-right">
                    {#if !token.revokedAt}
                      {#if revokingId === token.id}
                        <div class="flex items-center justify-end space-x-1">
                          <button class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg text-xs font-medium" onclick={() => revokeToken(token.id)}>{$_('common.confirm')}</button>
                          <button class="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" onclick={() => revokingId = null}><X size={16} /></button>
                        </div>
                      {:else}
                        <button class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg" onclick={() => revokingId = token.id} title={$_('ingestTokens.revoke')}>
                          <Trash2 size={18} />
                        </button>
                      {/if}
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    {/if}
  </div>
</Layout>
